/**
 * Pine Script Editor
 * Write and test Pine Script strategies
 *
 * Version: 6.2.0
 */
import React, { useState, useEffect, useRef } from 'react'
import { Code, Play, Save, Copy, FileCode, BookOpen, Terminal, CheckCircle, AlertCircle, Info } from 'lucide-react'
import './PineScriptEditor.css'

// Sample Pine Script templates
const TEMPLATES = [
  {
    id: 'ema-crossover',
    name: 'EMA Crossover',
    description: 'Simple EMA crossover strategy',
    code: `//@version=5
strategy("EMA Crossover", overlay=true)

// Define EMAs
fastEMA = ta.ema(close, 9)
slowEMA = ta.ema(close, 21)

// Plot EMAs
plot(fastEMA, color=color.green, title="Fast EMA")
plot(slowEMA, color=color.red, title="Slow EMA")

// Entry conditions
longCondition = ta.crossover(fastEMA, slowEMA)
shortCondition = ta.crossunder(fastEMA, slowEMA)

// Execute strategy
if (longCondition)
    strategy.entry("Long", strategy.long)

if (shortCondition)
    strategy.close("Long")
`
  },
  {
    id: 'rsi-reversal',
    name: 'RSI Reversal',
    description: 'RSI overbought/overserved reversal',
    code: `//@version=5
strategy("RSI Reversal", overlay=false)

// RSI settings
rsiLength = input.int(14, "RSI Length")
oversold = input.int(30, "Oversold Level")
overbought = input.int(70, "Overbought Level")

// Calculate RSI
rsi = ta.rsi(close, rsiLength)

// Plot RSI
plot(rsi, color=color.purple, title="RSI")
hline(overbought, "Overbought", color=color.red)
hline(oversold, "Oversold", color=color.green)

// Entry signals
longCondition = ta.crossover(rsi, oversold)
shortCondition = ta.crossunder(rsi, overbought)

if (longCondition)
    strategy.entry("Long", strategy.long)

if (shortCondition)
    strategy.entry("Short", strategy.short)
`
  },
  {
    id: 'macd-signal',
    name: 'MACD Signal',
    description: 'MACD histogram signal',
    code: `//@version=5
strategy("MACD Signal", overlay=true)

// MACD settings
[macdLine, signalLine, histLine] = ta.macd(close, 12, 26, 9)

// Plot
plot(macdLine, color=color.blue, title="MACD")
plot(signalLine, color=color.orange, title="Signal")
plot(histLine, color=color.red, style=plot.style_histogram, title="Histogram")

// Strategy
longCondition = ta.crossover(macdLine, signalLine)
shortCondition = ta.crossunder(macdLine, signalLine)

if (longCondition)
    strategy.entry("Long", strategy.long)

if (shortCondition)
    strategy.entry("Short", strategy.short)
`
  },
  {
    id: 'supertrend',
    name: 'Supertrend',
    description: 'Supertrend trend following',
    code: `//@version=5
strategy("Supertrend", overlay=true, default_qty_type=strategy.percent_of_equity, default_qty_value=10)

// Supertrend settings
atrPeriod = input(10, "ATR Length")
factor = input.float(3.0, "Factor")

[supertrend, direction] = ta.supertrend(factor, atrPeriod)

plot(supertrend, color=direction < 0 ? color.green : color.red, title="Supertrend")

// Entry
longCondition = direction < 0 and direction[1] >= 0
shortCondition = direction > 0 and direction[1] <= 0

if (longCondition)
    strategy.entry("Long", strategy.long)

if (shortCondition)
    strategy.entry("Short", strategy.short)
`
  },
]

const PineScriptEditor: React.FC = () => {
  const [code, setCode] = useState<string>(TEMPLATES[0].code)
  const [template, setTemplate] = useState<string>(TEMPLATES[0].id)
  const [output, setOutput] = useState<string[]>([])
  const [saved, setSaved] = useState(false)
  const [compiling, setCompiling] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setOutput([`[INFO] Loaded template: ${TEMPLATES.find(t => t.id === template)?.name}`])
  }, [template])

  const handleTemplateChange = (id: string) => {
    setTemplate(id)
    const selected = TEMPLATES.find(t => t.id === id)
    if (selected) {
      setCode(selected.code)
      setOutput([`[INFO] Loaded template: ${selected.name}`])
    }
  }

  const handleCompile = () => {
    setCompiling(true)
    setOutput([])
    
    setTimeout(() => {
      const lines: string[] = []
      lines.push('[INFO] Compiling Pine Script...')
      
      // Basic validation
      if (!code.includes('//@version=')) {
        lines.push('[ERROR] Missing version directive')
      } else {
        lines.push('[SUCCESS] Version directive found')
      }
      
      if (code.includes('strategy(') || code.includes('indicator(')) {
        lines.push('[SUCCESS] Script type detected')
      } else {
        lines.push('[ERROR] Missing strategy or indicator declaration')
      }
      
      if (code.includes('ta.')) {
        const indicators = code.match(/ta\.\w+/g) || []
        lines.push(`[INFO] Using ${indicators.length} TradingView functions`)
      }
      
      lines.push('[SUCCESS] Compilation complete - No errors found')
      
      setOutput(lines)
      setCompiling(false)
    }, 1000)
  }

  const handleSave = () => {
    localStorage.setItem('pinescript-code', code)
    setSaved(true)
    setOutput(prev => [...prev, '[INFO] Code saved to browser storage'])
    setTimeout(() => setSaved(false), 2000)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setOutput(prev => [...prev, '[INFO] Code copied to clipboard'])
  }

  const handleClear = () => {
    setCode('')
    setOutput(['[INFO] Editor cleared'])
  }

  // Tab handling in textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const start = e.currentTarget.selectionStart
      const end = e.currentTarget.selectionEnd
      const newCode = code.substring(0, start) + '  ' + code.substring(end)
      setCode(newCode)
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + 2
        }
      }, 0)
    }
  }

  return (
    <div className="pine-editor-page">
      <div className="page-header">
        <div className="header-content">
          <h1><Code className="page-icon" /> Pine Script Editor</h1>
          <p className="page-subtitle">Write and test Pine Script strategies</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCompile} disabled={compiling}>
            <Terminal size={16} />
            {compiling ? 'Compiling...' : 'Compile'}
          </button>
          <button className="btn btn-success" onClick={handleSave}>
            <Save size={16} />
            {saved ? 'Saved!' : 'Save'}
          </button>
          <button className="btn btn-secondary" onClick={handleCopy}>
            <Copy size={16} />
            Copy
          </button>
        </div>
      </div>

      <div className="editor-layout">
        {/* Sidebar - Templates */}
        <div className="templates-sidebar">
          <h3><BookOpen size={16} /> Templates</h3>
          {TEMPLATES.map(t => (
            <div
              key={t.id}
              className={`template-item ${template === t.id ? 'active' : ''}`}
              onClick={() => handleTemplateChange(t.id)}
            >
              <FileCode size={16} />
              <div className="template-info">
                <div className="template-name">{t.name}</div>
                <div className="template-desc">{t.description}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Editor */}
        <div className="editor-main">
          <div className="editor-toolbar">
            <span className="file-name">strategy.pine</span>
            <button className="btn-icon" onClick={handleClear} title="Clear">
              ×
            </button>
          </div>
          <div className="code-editor">
            <div className="line-numbers">
              {code.split('\n').map((_, i) => (
                <div key={i} className="line-num">{i + 1}</div>
              ))}
            </div>
            <textarea
              ref={textareaRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              className="code-input"
            />
          </div>
        </div>

        {/* Output Panel */}
        <div className="output-panel">
          <h3><Terminal size={16} /> Output</h3>
          <div className="output-content">
            {output.map((line, i) => (
              <div 
                key={i} 
                className={`output-line ${
                  line.includes('[ERROR]') ? 'error' : 
                  line.includes('[SUCCESS]') ? 'success' : 'info'
                }`}
              >
                {line.includes('[ERROR]') && <AlertCircle size={14} />}
                {line.includes('[SUCCESS]') && <CheckCircle size={14} />}
                {line.includes('[INFO]') && <Info size={14} />}
                <span>{line.replace(/^\[.*?\]\s*/, '')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="help-section">
        <h3>Pine Script v5 Reference</h3>
        <div className="help-grid">
          <div className="help-card">
            <h4>Functions</h4>
            <code>ta.ema(source, length)</code>
            <code>ta.rsi(source, length)</code>
            <code>ta.macd(source, fast, slow, signal)</code>
            <code>ta.supertrend(factor, period)</code>
            <code>ta.crossover(source1, source2)</code>
          </div>
          <div className="help-card">
            <h4>Indicators</h4>
            <code>plot(value)</code>
            <code>hline(value)</code>
            <code>fill(plot1, plot2)</code>
            <code>plotshape(series)</code>
            <code>bgcolor(color)</code>
          </div>
          <div className="help-card">
            <h4>Strategy</h4>
            <code>strategy.entry(id, direction)</code>
            <code>strategy.exit(id, from_entry)</code>
            <code>strategy.close(id)</code>
            <code>strategy.order(id, side)</code>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PineScriptEditor
